import React, {ReactElement} from "react";

import invariant from "/src/utils/invariant";
import {slugify, visibleForLocale} from "/src/utils/utils";
import translate from "/src/utils/translate";

import WistiaChannel from "../videos/wistia-channel/WistiaChannel";
import LazyLoadCardLayout, {
  LazyLoadCardLayoutCard,
} from "../layouts/lazy-load-card-layout/LazyLoadCardLayout";
import {ColumnValue} from "../layouts/card-layout/CardLayout";

import * as styles from "./featured-content-module.module.scss";
import {LOCALE} from "/src/config";
import {CardFlow} from "../cards/card/Card";

const FeaturedContentModule = (
  props: Queries.FeaturedContentModuleFragmentFragment
): ReactElement | null => {
  const {
    visibleIn,
    useWistiaChannel,
    wistiaChannelId,
    copy,
    darkMode,
    eyebrow,
    title,
    column = 3,
    useCardStyle,
    defaultVisibility = 1,
    featuredLayout = false,
    openInNewTab = false,
    isImageIcon = false,
    loadMoreButtonText,
    cards,
    cardFlow,
    filterable = false,
    squareImage = false,
    anchorLinkId,
  } = props;

  const columnStr = (column ? column : 3).toString();

  const isDarkMode: boolean = darkMode === true;

  if (!visibleForLocale(visibleIn as Array<string>, LOCALE)) {
    return null;
  }

  if (useWistiaChannel && wistiaChannelId) {
    return (
      <WistiaChannel
        copy={copy?.copy!}
        headline={title!}
        id={wistiaChannelId}
        darkMode={isDarkMode}
      />
    );
  }

  invariant(
    columnStr === "2" || columnStr === "3" || columnStr === "4",
    "Invalid column number"
  );

  let cardImageStyle = styles.featuredContentModule__cardImg;

  if (squareImage === true) {
    cardImageStyle = styles.featuredContentModule__cardImgSquareImg;
  } else if (useCardStyle === true) {
    cardImageStyle = styles.featuredContentModule__cardImgCardStyle;
  } else if (isImageIcon === true) {
    cardImageStyle = styles.featuredContentModule__cardImgIcon;
  }

  return (
    <LazyLoadCardLayout
      cardFlow={cardFlow as CardFlow}
      cardImageClasses={cardImageStyle}
      column={columnStr as ColumnValue}
      eyebrow={eyebrow!}
      headline={title!}
      copy={copy?.copy!}
      cards={(cards || []) as Array<LazyLoadCardLayoutCard>}
      buttonText={loadMoreButtonText || translate("Load More")}
      darkMode={isDarkMode}
      numVisibleRows={defaultVisibility || 1}
      isImageIcon={isImageIcon === true}
      openInNewTab={openInNewTab === true}
      useCardStyle={useCardStyle === true}
      filterable={filterable === true}
      featuredLayout={featuredLayout === true}
      id={slugify(title!)}
      anchorLinkId={anchorLinkId}
    />
  );
};

export default FeaturedContentModule;
